import { AuthenticatedUserContext, CrfFormByCodesRoute, FormMapping, FormQueryOptionsContext, IFormQueryOptions, IMenuItem, InstitutionsByCodesRoute, OnlinePatientManagementContentProvider, OnlinePatientManagementContext, PatientDetailsByStudyNumberRoute, QueryType, RouteLoading, useIsMobile } from "@ngt/opms";
import { ReactElement, useContext, useMemo } from "react";

import { faQuestion, faUser } from '@fortawesome/pro-duotone-svg-icons';
import { faCopyright } from '@fortawesome/pro-regular-svg-icons/faCopyright';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Typography } from "@mui/material";
import { Permission, usePermissionsByIds } from "@ngt/opms-bctapi";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";
import { AppSettings } from "./AppSettings";
import ToggleFeedbackButton from "./components/buttons/ToggleFeedbackButton";
import BaselineDemographicsForm from "./components/forms/crf/BaselineDemographicsForm";
import BloodCollectionForm from "./components/forms/crf/BloodCollectionForm";
import EndOfTreatmentForm from "./components/forms/crf/EndOfTreatmentForm";
import VisitDetailsForm from "./components/forms/crf/VisitDetailsForm";
import ErrorPage, { ErrorTypeEnum } from "./containers/ErrorPage";
import PatientQueriesPage from "./containers/PatientQueries";
import PatientSummaryPage from "./containers/PatientSummaryPage";
import Locales from "./containers/public/Locales";
import RegistrationPage from "./containers/RegistrationPage";
import QueriesPage from "./containers/QueriesPage";
import AllQueriesProvider from "./components/providers/AllQueriesProvider";
import RegistrationForm from "./components/forms/crf/RegistrationForm";
import WithdrawalForm from "./components/forms/crf/WithdrawalForm";
import useSentryUser from "./hooks/sentry/useSentryUser";
import AdverseEventsForm from "./components/forms/crf/AdverseEventsForm";

const formMapping: Record<string, ReactElement> = {
    "registration": <RegistrationForm />,
    "baseline-demographics": <BaselineDemographicsForm />,
    "blood-collection": <BloodCollectionForm />,
    "visit-details": <VisitDetailsForm />,
    "end-of-treatment": <EndOfTreatmentForm />,
    "withdrawal": <WithdrawalForm />,
    "adverse-events": <AdverseEventsForm />
}

const App = () => {
    useSentryUser();
    const { data: user } = useContext(AuthenticatedUserContext);
    const { routeParameters } = useContext(OnlinePatientManagementContext);
    
    const isMobile = useIsMobile();

    const {
        permissions: [
            canAccessOpms,
            canOpmsAdmin,
            canDataManage,
            canViewQuerySummaries
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsAccess,
            Permission.OpmsAdminister,
            Permission.OpmsDataManage,
            Permission.CanViewQuerySummaries
        ]
    );

    const canAdministerOpms = useMemo(() => {
        return canDataManage || canOpmsAdmin;
    }, [canDataManage, canOpmsAdmin]);

    const formQueryOptionsContext: IFormQueryOptions = useMemo(() => ({
        canOpen: !permissionsLoading && canAdministerOpms,
        canProposeResolution: !permissionsLoading && canAccessOpms && !canAdministerOpms,
        canClose: !permissionsLoading && canAdministerOpms,
        titleOptions: {
            [QueryType.Query]: [
                "Missing Data",
                "Review Data Entry",
                "Source Data",
                "Medical Review Query",
                "Submit Form",
                "Other"
            ],
            [QueryType.ProtocolDeviation]: undefined
        }
    }), [permissionsLoading, canAdministerOpms]);

    const menuItems: IMenuItem[] = useMemo(() => {
        if (!user) {
            return [];
        }

        let items: IMenuItem[] = [];

        if (canAdministerOpms || canViewQuerySummaries) {
            items.push(
                {
                    url: '/queries',
                    label: 'Query Summary',
                    external: false,
                    newTab: false,
                    icon: faQuestion as any
                }
            );
        }

        items.push(
            {
                url: '/registration',
                label: 'Participants',
                external: false,
                newTab: false,
                icon: faUser as any
            }
        );

        return items;
    }, [canAdministerOpms, canViewQuerySummaries, user]);
    
    const additionalHeaderComponents = useMemo(() => {
        return (
            AppSettings.sentry.dsn && 
            AppSettings.sentry.enableReportABug ?
                [<ToggleFeedbackButton />] :
                undefined
        );
    }, []);

    const invalidPermission = user && !permissionsLoading && !canAccessOpms;

    if (permissionsLoading && user) {
        return (
            <RouteLoading />
        );
    }

    if (invalidPermission) {
        return (
            <OnlinePatientManagementContentProvider
                menuItems={menuItems}
                footer={
                    isMobile ?
                        undefined :
                        <Typography variant="caption">
                            Copyright <FontAwesomeIcon icon={faCopyright} fixedWidth /> {(new Date().getFullYear())} ANZ Breast Cancer Trials Group Ltd | ABN 64 051 369 496
                        </Typography>
                }
                additionalHeaderComponents={additionalHeaderComponents}
            >
                <ErrorPage errorType={ErrorTypeEnum.e403} />
            </OnlinePatientManagementContentProvider>
        );
    }

    // if (!user) { 
    //     return (
    //         <Routes>
    //             <Route 
    //                 path="/" 
    //                 element={
    //                     <OnlinePatientManagementContentProvider
    //                         menuItems={menuItems}
    //                         footer={
    //                             isMobile ?
    //                                 undefined :
    //                                 <Typography variant="caption">
    //                                     Copyright <FontAwesomeIcon icon={faCopyright} fixedWidth /> {(new Date().getFullYear())} ANZ Breast Cancer Trials Group Ltd | ABN 64 051 369 496
    //                                 </Typography>
    //                         }
    //                     >
    //                         <Outlet />
    //                     </OnlinePatientManagementContentProvider>
    //                 }
    //             >    
    //                 <Route
    //                     path="participant-information"
    //                     element={<Outlet />}
    //                 >
    //                     <Route index element={<TrialInformationPage />} />      
    //                 </Route>
    //             </Route>
    //         </Routes>
    //     )
    // }
    
    return (
        <Routes>
            <Route 
                path="/" 
                element={
                    <OnlinePatientManagementContentProvider
                        menuItems={menuItems}
                        footer={
                            isMobile ?
                                undefined :
                                <Typography variant="caption">
                                    Copyright <FontAwesomeIcon icon={faCopyright} fixedWidth /> {(new Date().getFullYear())} ANZ Breast Cancer Trials Group Ltd | ABN 64 051 369 496
                                </Typography>
                        }
                        additionalHeaderComponents={additionalHeaderComponents}
                    >
                        <Outlet />
                    </OnlinePatientManagementContentProvider>
                }
            >
                <Route
                    path="admin"
                    element={<Outlet />}
                >
                    <Route
                        path="manage-locales"
                        element={<Outlet />}
                    >
                        <Route
                            index
                            element={<Locales />}
                        />
                    </Route>
                </Route>
                <Route
                    path="queries"
                    element={
                        <FormQueryOptionsContext.Provider value={formQueryOptionsContext}>
                            <InstitutionsByCodesRoute 
                                loadBeforeMount 
                            >
                                <Outlet />
                            </InstitutionsByCodesRoute>
                        </FormQueryOptionsContext.Provider>
                    }
                >
                    <Route 
                        index
                        element={<QueriesPage />}
                    />
                </Route>
                {/* <Route
                    path="participant-information"
                    element={<Outlet />}
                >
                    <Route index element={<TrialInformationPage />} />      
                </Route> */}
                <Route 
                    path="registration" 
                    element={
                        <InstitutionsByCodesRoute 
                            loadBeforeMount 
                        >
                            <Outlet />
                        </InstitutionsByCodesRoute>
                    }
                >
                    <Route index element={
                        <AllQueriesProvider>
                            <RegistrationPage />
                        </AllQueriesProvider>
                    } />
                    <Route path={`:${routeParameters.institutionCode}`}>
                        <Route index element={
                            <AllQueriesProvider>
                                <RegistrationPage />
                            </AllQueriesProvider>
                        } />
                        <Route 
                            path={`:${routeParameters.patientStudyNumber}`} 
                            element={
                                <PatientDetailsByStudyNumberRoute 
                                    loadBeforeMount
                                >
                                    <Outlet />
                                </PatientDetailsByStudyNumberRoute>
                            }
                        >
                            <Route index element={   
                                <PatientSummaryPage />
                            }/>
                            <Route 
                                path={"queries"}
                                element={
                                    <FormQueryOptionsContext.Provider value={formQueryOptionsContext}>
                                        <Outlet />
                                    </FormQueryOptionsContext.Provider>
                                }
                            >
                                <Route 
                                    index
                                    element={<PatientQueriesPage />}
                                />
                            </Route>
                            <Route 
                                path={`:${routeParameters.eventDefinitionCode}/:${routeParameters.eventRepeat}/:${routeParameters.formDefinitionCode}/:${routeParameters.formRepeat}`}
                                element={
                                    <CrfFormByCodesRoute
                                        loadBeforeMount
                                        validateBeforeMount={{
                                            form: true
                                        }}
                                        createEvent
                                        createForm
                                    >
                                        <FormQueryOptionsContext.Provider value={formQueryOptionsContext}>
                                            <FormMapping mapping={formMapping} />
                                        </FormQueryOptionsContext.Provider>
                                    </CrfFormByCodesRoute>
                                }
                            />
                        </Route>
                    </Route>
                </Route>
                <Route path="error/403" element={<ErrorPage errorType={ErrorTypeEnum.e403} />} />
                <Route path="error/404" element={<ErrorPage errorType={ErrorTypeEnum.e404} />} />
                <Route path="error/405" element={<ErrorPage errorType={ErrorTypeEnum.e405} />} />
                <Route path="error/500" element={<ErrorPage errorType={ErrorTypeEnum.e500} />} />
                <Route index element={<Navigate to="/registration" replace />} />
                <Route path="*" element={<Navigate to="/registration" replace />} />
            </Route>
        </Routes>
    );
}

export default App;